import type {
  DefaultizedPieValueType,
  PieItemId,
  PieItemIdentifier,
} from '@mui/x-charts/models';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useRef } from 'react';
import type { InteractionType } from '@/common/types/interaction';
import {
  getInteractionsFiltersKey,
  interactionsAgentsState,
  interactionsStatusesState,
  useSetDateCreatedFilterState,
} from '@/views/Interactions/state/filters';
import {
  TrackHomePageEvent,
  useTrackHomePageAnalytics,
} from '$analytics/useTrackAnalytics/domains/homePage';
import { getLabel } from '../utils';
import { useFactsAndFiguresContext } from './FactsAndFiguresContext';

export function useRouteToTLV(
  interactionType: InteractionType,
  isMobile: boolean,
) {
  const router = useRouter();
  const lastClickedStatus = useRef<PieItemId | null>(null);
  const stateKey = getInteractionsFiltersKey(interactionType);
  const setStatuses = useSetAtom(interactionsStatusesState(stateKey));
  const updateDateCreatedFilter = useSetDateCreatedFilterState(stateKey);
  const setAgentFilters = useSetAtom(interactionsAgentsState(stateKey));
  const trackEvent = useTrackHomePageAnalytics();

  const { agents, dateRange, startDate, endDate } = useFactsAndFiguresContext();

  const pluralInteractionType = `${interactionType}s`;

  const routeToTLVPageWithFiltersApplied = (
    _: React.MouseEvent<SVGPathElement, MouseEvent>,
    __: PieItemIdentifier,
    item: DefaultizedPieValueType,
  ) => {
    const { id = '', label = '', value = 0 } = item;

    const isSecondClick = lastClickedStatus.current === id;
    const isReadyToRoute = isMobile ? isSecondClick : true;

    if (isMobile && !isReadyToRoute) {
      lastClickedStatus.current = id;
      trackEvent(TrackHomePageEvent.FactsAndFiguresMobileSegmentTapClick, {
        numItem: value,
        interactionType,
        interactionStatus: label,
      });
    }

    if (isReadyToRoute) {
      if (!isMobile) {
        trackEvent(TrackHomePageEvent.FactsAndFiguresSegmentClick, {
          numItem: value,
          interactionType,
          interactionStatus: label,
          dateType: dateRange,
          startDate,
          endDate,
          agentId: agents.map((agent) => agent.value).join(','),
        });
      }
      // Status Filters
      setStatuses([
        {
          label: getLabel(label),
          value: `status:${id}`,
        },
      ]);

      // Date Filters
      updateDateCreatedFilter({
        dateRange,
        startDate,
        endDate,
      });

      // Agent Filters
      setAgentFilters(
        agents.map((agent) => ({
          label: agent.label,
          value: `teamMemberId:${agent.value}`,
        })),
      );

      void router.push(`/${pluralInteractionType}`);
    }
  };

  const clearLastClicked = () => {
    lastClickedStatus.current = null;
  };

  return { routeToTLVPageWithFiltersApplied, clearLastClicked };
}
